import { genericGETDatabaseCall, genericPOSTDatabaseCall, RenderOneTable, RenderProjectionTable } from "../helperFunctions";
import { Rnd } from 'react-rnd';
import { useLocation } from "react-router";
import { useState, useContext, useCallback, useEffect } from 'react';
import { Switch, message } from "antd";
import { SaveOutlined } from "@ant-design/icons"
import {
    ComposedChart,
    Bar,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer
} from 'recharts';

import '../CriticalFinance/CriticalFinanceTable.css'
import '../TemplateRender/TemplateRender.css'

async function saveDashboardEntityToServer(entity) {
    return genericPOSTDatabaseCall('/api/psbb/addDashboardEntity', { dashboard: entity }, 'dashboard entity');
}

async function getDashboardEntityFromServer(templateID) {
    return genericGETDatabaseCall('/api/psbb/getDashboardEntity?sharedLinkUUID=' + templateID, 'dashboard entity');
}

export function TemplateRenderWithContext({ context }) {
    const { setPageNum, mergedData, messageApi, entitySelected} = useContext(context);

    return (
        <TemplateRenderBase
            setPageNum={setPageNum}
            mergedData={mergedData}
            messageApi={messageApi}
            withContext={true}
            entitySelected={entitySelected}
        />
    );
}

export default function TemplateRenderWithTemplateID() {
    const location = useLocation();
    const templateId = (new URLSearchParams(location.search)).get('templateId');
    const [messageApi, contextHolder] = message.useMessage();
    const [mergedData, setMergedData] = useState({
        'data': [],
        'layout': []
    });
    const [loading, setLoading] = useState(true);

    const getTemplateData = useCallback(async () => {
        messageApi.loading("Getting dashboard data...");
        try {
            const data = await getDashboardEntityFromServer(templateId);
            setMergedData(data);
            messageApi.destroy();
            messageApi.success("Loaded dashboard data");
            setLoading(false);
        } catch (err) {
            messageApi.destroy();
            messageApi.error("Unable to get dashboard data");
            console.error(err);
        }
    }, [messageApi])

    useEffect(() => {
        getTemplateData();
    }, [getTemplateData])

    return (
        <>
        {  !loading &&
            <TemplateRenderBase 
                mergedData={mergedData.data} 
                withContext={false} 
                initLayout={mergedData.layout} 
                dashboardNameFromParent={mergedData.info.name}
            />
        }
        {
            loading && <h3>Loading Dashboard.....</h3>
        }
        </>
    );
}


function TemplateRenderBase({ setPageNum, mergedData, messageApi, withContext, initLayout, dashboardNameFromParent, entitySelected }) {
    const [editMode, setEditMode] = useState(false);
    const [dashboardName, setDashboardName] = useState('')

    // Dynamically initialize positions for all widgets
    const [widgetStates, setWidgetStates] = useState(() => {
        if (!withContext) return initLayout;
        const initialPositions = {};

        mergedData.forEach((metric, index) => {
            initialPositions[metric.id] = {
                x: 20 + (index % 3) * 320,
                y: 20 + Math.floor(index / 3) * 240,
                width: 'auto',
                height: 'auto',
            };
        });
        return initialPositions;
    });

    const handleDragStop = (metricId, d) => {
        setWidgetStates((prev) => ({
            ...prev,
            [metricId]: {
                ...prev[metricId],
                x: d.x,
                y: d.y,
            },
        }));
    };

    const handleResizeStop = (metricId, ref, position) => {
        setWidgetStates((prev) => ({
            ...prev,
            [metricId]: {
                ...prev[metricId],
                width: ref.offsetWidth,
                height: ref.offsetHeight,
                x: position.x,
                y: position.y,
            },
        }));
    };

    const saveDashboardEntity = async () => {
        messageApi.loading("Saving dashboard entity to dashboard...", 0);
        try {
            await saveDashboardEntityToServer({ layout: widgetStates, data: mergedData, 
                info: {
                    'name': dashboardName,
                    'entity_name': entitySelected.name
                } 
            });
            messageApi.destroy();
            messageApi.success("Dashboard entity saved");
        } catch (err) {
            messageApi.destroy();
            messageApi.error("Failed to dashboard entity");
            console.error(err);
        }
    }

    return (
        <>
            
            <div className="header-mode-container navigation-button-container">
                {withContext && <>
                    <button type="button" onClick={() => setPageNum(4)} class="next-button">Back</button>
                    <div className="dashboard-name flex-row">
                        <input onChange={(event) => setDashboardName(event.target.value)} value={dashboardName} placeholder="Edit Dashboard Name......" type="text" />
                    </div>
                    <div>
                        <div style={{ marginRight: '10px', display: 'inline-block' }} class="next-button">
                            <span>Edit mode</span>
                            <Switch onChange={() => setEditMode(!editMode)} />
                        </div>
                        <button onClick={saveDashboardEntity} style={{ display: 'inline-flex', alignItems: 'center' }} type="button" class="next-button">
                            <span style={{ marginRight: '10px' }}>Save to Dashboard</span>
                            <SaveOutlined style={{ fontSize: "20px" }} />
                        </button>
                    </div>
                </>}
                {!withContext && <>
                    <div className="header-name">{dashboardNameFromParent}</div>
                </>}
            </div>
            <div className="entity-industry-container dashboard-maker" style={{ position: 'relative', minHeight: '200vh', maxWidth: '95%' }}>
                <div style={{ minHeight: '200vh' }}>
                    {mergedData.map((metric) => (
                        <Rnd
                            key={metric.id}
                            size={{ width: widgetStates[metric.id].width, height: widgetStates[metric.id].height }}
                            position={{ x: widgetStates[metric.id].x, y: widgetStates[metric.id].y }}
                            onDragStop={(e, d) => handleDragStop(metric.id, d)}
                            onResizeStop={(e, direction, ref, delta, position) => handleResizeStop(metric.id, ref, position)}
                            width={'fit-content'}
                            height={'fit-content'}
                            disableDragging={!editMode}
                            bounds="parent"
                            enableResizing={editMode === true ? {
                                top: true,
                                right: true,
                                bottom: true,
                                left: false,
                                topRight: true,
                                bottomRight: true,
                                bottomLeft: true,
                                topLeft: false,
                            } : {}}
                            style={{
                                boxSizing: 'border-box',
                                border: editMode ? '2px solid #ff0606c7' : '1px solid transparent',
                                transition: 'border-color 0.2s',
                                overflow: 'hidden',
                            }}
                            resizeHandleStyles={{
                                top: {
                                    background: '#ff0606c7',
                                    height: '8px',
                                    top: '-4px',
                                    left: 0,
                                    right: 0,
                                    cursor: 'n-resize',
                                },
                                right: {
                                    background: '#ff0606c7',
                                    width: '8px',
                                    right: '-4px',
                                    top: 0,
                                    bottom: 0,
                                    cursor: 'e-resize',
                                },
                                bottom: {
                                    background: '#ff0606c7',
                                    height: '8px',
                                    bottom: '-4px',
                                    left: 0,
                                    right: 0,
                                    cursor: 's-resize',
                                },
                                topRight: {
                                    background: '#ff0606c7',
                                    width: '20px',
                                    height: '20px',
                                    borderRadius: '3px',
                                    top: '-10px',
                                    right: '-10px',
                                    cursor: 'ne-resize',
                                },
                                bottomRight: {
                                    background: '#ff0606c7',
                                    width: '20px',
                                    height: '20px',
                                    borderRadius: '3px',
                                    bottom: '-10px',
                                    right: '-10px',
                                    cursor: 'se-resize',
                                },
                                bottomLeft: {
                                    background: '#ff0606c7',
                                    width: '20px',
                                    height: '20px',
                                    borderRadius: '3px',
                                    bottom: '-10px',
                                    left: '-10px',
                                    cursor: 'sw-resize',
                                },
                            }}
                        >
                            <div style={{ width: '100%', height: '100%', position: 'relative', cursor: 'move' }}>
                                <RenderMetric metric={metric} />
                            </div>
                        </Rnd>
                    ))}
                </div>
            </div>
        </>
    )
}

function RenderMetric({ metric }) {
    switch (metric.data_type) {
        case ("Big Number Card"): {
            return <BigNumberCard data={metric.data} name={metric.name} />
        }
        case ("Pie Chart"): {
            return <PieChart data={metric.data} name={metric.name} />
        }
        case ("Waterfall Chart"): {
            return <WaterfallChart data={metric.data} name={metric.name} />
        }
        case ("Chart (Projections)"): {
            return <ProjectionsChart data={metric.data} name={metric.name} />
        }
        case ("Two Col Table"): {
            return <RenderOneTable tableStyles={{ margin: '0px', height: '100%', fontSize: '11px' }} styles={{ margin: '0px', height: '100%' }} data={metric.data} name={metric.name} />
        }
        case ("More Than Two Col Table"): {
            return <RenderProjectionTable tableStyles={{ margin: '0px', height: '100%', fontSize: '11px' }} styles={{ margin: '0px', height: '100%' }} data={metric.data} name={metric.name} />
        }
        default: {
            return <pre>{JSON.stringify(metric, null, 2)}</pre>
        }
    }
}

function BigNumberCard({ data, name }) {
    // Extract percentage value if it's a currency string
    const displayValue = data;
    const numericValue = typeof data === 'string' ? parseFloat(data.replace(/[^0-9.-]/g, '')) : data;
    const percentage = Math.min(Math.abs(numericValue), 100);

    return (
        <div style={{
            background: '#1a3a52',
            borderRadius: '11px',
            padding: '19px 21px',
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.2)',
            color: 'white',
            minHeight: '120px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            position: 'relative',
            transition: 'transform 0.3s ease, box-shadow 0.3s ease'
        }}
            onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-3px)';
                e.currentTarget.style.boxShadow = '0 12px 32px rgba(0, 0, 0, 0.3)';
            }}
            onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 8px 24px rgba(0, 0, 0, 0.2)';
            }}>
            <div style={{
                position: 'absolute',
                top: '13px',
                right: '21px',
                fontSize: '9px',
                fontWeight: '600',
                color: '#ccc',
                letterSpacing: '0.5px'
            }}>
                Firm Wide
            </div>

            <div style={{ flex: 1 }}>
                <div style={{
                    fontSize: '38px',
                    fontWeight: '700',
                    letterSpacing: '-2px',
                    marginBottom: '5px',
                    color: 'white'
                }}>
                    {displayValue}
                </div>
                <div style={{
                    fontSize: '11px',
                    fontWeight: '500',
                    color: '#b8d4e8',
                    letterSpacing: '0.3px'
                }}>
                    {name}
                </div>
            </div>

            <div style={{ position: 'relative', width: '80px', height: '80px' }}>
                <svg width="80" height="80" viewBox="0 0 120 120" style={{ transform: 'rotate(-90deg)' }}>
                    {/* Background arc */}
                    <circle
                        cx="60"
                        cy="60"
                        r="50"
                        fill="none"
                        stroke="#2a4a62"
                        strokeWidth="16"
                        strokeDasharray="157 314"
                        strokeLinecap="round"
                    />
                    {/* Progress arc */}
                    <circle
                        cx="60"
                        cy="60"
                        r="50"
                        fill="none"
                        stroke="#5dade2"
                        strokeWidth="16"
                        strokeDasharray={`${(percentage / 100) * 157} 314`}
                        strokeLinecap="round"
                        style={{ transition: 'stroke-dasharray 0.6s ease' }}
                    />
                </svg>
            </div>
        </div>
    );
}

function PieChart({ data, name }) {
    // data is an array of { label: string, value: number (0-1 or percentage) }
    const colors = ['#5dade2', '#45b7d1', '#f39c12', '#e74c3c', '#9b59b6', '#1abc9c', '#34495e', '#16a085'];

    // Convert values to percentages if needed
    const normalizedData = data.map(item => ({
        ...item,
        value: item.value <= 1 ? item.value : item.value / 100
    }));

    // Calculate pie slices
    let currentAngle = 0;
    const slices = normalizedData.map((item, index) => {
        const percentage = item.value;
        const angle = percentage * 360;
        const slice = {
            ...item,
            startAngle: currentAngle,
            endAngle: currentAngle + angle,
            percentage: percentage * 100,
            color: colors[index % colors.length]
        };
        currentAngle += angle;
        return slice;
    });

    // Create SVG path for pie slice
    const createArc = (startAngle, endAngle, radius = 100) => {
        const start = polarToCartesian(120, 120, radius, endAngle);
        const end = polarToCartesian(120, 120, radius, startAngle);
        const largeArcFlag = endAngle - startAngle <= 180 ? '0' : '1';
        return `M 120 120 L ${start.x} ${start.y} A ${radius} ${radius} 0 ${largeArcFlag} 0 ${end.x} ${end.y} Z`;
    };

    const polarToCartesian = (centerX, centerY, radius, angleInDegrees) => {
        const angleInRadians = ((angleInDegrees - 90) * Math.PI) / 180.0;
        return {
            x: centerX + radius * Math.cos(angleInRadians),
            y: centerY + radius * Math.sin(angleInRadians)
        };
    };

    return (
        <div style={{
            background: 'white',
            borderRadius: '11px',
            padding: '21px',
            boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: '32px',
            overflow: 'auto'
        }}>
            <div style={{ position: 'relative', width: '120px', height: '120px', flexShrink: 0 }}>
                <svg width={120} height={120} viewBox="0 0 240 240">
                    {slices.map((slice, index) => (
                        <g key={index}>
                            <path
                                d={createArc(slice.startAngle, slice.endAngle)}
                                fill={slice.color}
                                style={{
                                    transition: 'transform 0.3s ease',
                                    transformOrigin: '120px 120px',
                                    cursor: 'pointer'
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.transform = 'scale(1.05)';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.transform = 'scale(1)';
                                }}
                            />
                        </g>
                    ))}
                    {/* Center white circle */}
                    <circle cx="120" cy="120" r="60" fill="white" />
                </svg>
            </div>

            <div style={{ flex: 1 }}>
                <h3 style={{
                    margin: '0 0 16px 0',
                    fontSize: '13px',
                    fontWeight: '600',
                    color: '#2c3e50'
                }}>
                    {name}
                </h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {slices.map((slice, index) => (
                        <div key={index} style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            padding: '5px 0'
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginRight: '7px' }}>
                                <div style={{
                                    width: '11px',
                                    height: '11px',
                                    borderRadius: '3px',
                                    backgroundColor: slice.color
                                }} />
                                <span style={{
                                    fontSize: '11px',
                                    color: '#34495e',
                                    fontWeight: '500'
                                }}>
                                    {slice.label}
                                </span>
                            </div>
                            <span style={{
                                fontSize: '11px',
                                fontWeight: '600',
                                color: '#2c3e50'
                            }}>
                                {slice.percentage.toFixed(1)}%
                            </span>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

function WaterfallChart({ data, name }) {
    // Parse numeric values from strings
    const parseValue = (val) => {
        if (val.includes("$")) val = val.replace("$", "");
        if (typeof val === 'number') return val;
        return parseFloat(val.replace(/,/g, '')) || 0;
    };

    // Determine chart type and calculate cumulative values
    const chartData = data.map((item, index) => {
        const numericValue = parseValue(item.value);
        const label = item.label;

        // First and last items are totals
        const isTotal = index === 0 || index === data.length - 1;

        // Determine if it's a positive or negative change based on label keywords
        const isNegative = label.toLowerCase().includes('churn') ||
            label.toLowerCase().includes('contraction') ||
            label.toLowerCase().includes('decrease');

        return {
            label,
            value: numericValue,
            displayValue: item.value,
            isTotal,
            isNegative: !isTotal && isNegative
        };
    });

    // Calculate cumulative positions
    let cumulative = 0;
    const bars = chartData.map((item, index) => {
        if (item.isTotal) {
            const bar = {
                ...item,
                start: 0,
                end: item.value,
                cumulative: item.value
            };
            cumulative = item.value;
            return bar;
        } else {
            const change = item.isNegative ? -item.value : item.value;
            const start = cumulative;
            cumulative += change;
            return {
                ...item,
                start: Math.min(start, cumulative),
                end: Math.max(start, cumulative),
                cumulative,
                change
            };
        }
    });

    const maxValue = Math.max(...bars.map(b => Math.max(b.end, b.start)));
    const chartHeight = 168;
    const barWidth = 30;
    const gap = 40;
    const chartWidth = bars.length * (barWidth + gap);

    const getY = (value) => {
        return chartHeight - (value / maxValue) * (chartHeight - 40);
    };

    return (
        <div style={{
            background: 'white',
            borderRadius: '11px',
            padding: '21px',
            boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)',
            overflow: 'auto'
        }}>
            <h3 style={{
                margin: '0 0 16px 0',
                fontSize: '11px',
                fontWeight: '600',
                color: '#2c3e50'
            }}>
                {name}
            </h3>
            <svg width={chartWidth} height={chartHeight + 80} style={{ minWidth: '100%' }}>
                {bars.map((bar, index) => {
                    const x = index * (barWidth + gap) + gap / 2;
                    const y1 = getY(bar.start);
                    const y2 = getY(bar.end);
                    const barHeight = Math.abs(y2 - y1);
                    const barY = Math.min(y1, y2);

                    let color;
                    if (bar.isTotal) {
                        color = '#34495e';
                    } else if (bar.isNegative) {
                        color = '#e74c3c';
                    } else {
                        color = '#27ae60';
                    }

                    return (
                        <g key={index}>
                            {/* Connector line to next bar */}
                            {index < bars.length - 1 && !bar.isTotal && (
                                <line
                                    x1={x + barWidth}
                                    y1={getY(bar.cumulative)}
                                    x2={x + barWidth + gap}
                                    y2={getY(bar.cumulative)}
                                    stroke="#bdc3c7"
                                    strokeWidth="2"
                                    strokeDasharray="4,4"
                                />
                            )}

                            {/* Bar */}
                            <rect
                                x={x}
                                y={barY}
                                width={barWidth}
                                height={Math.max(barHeight, 2)}
                                fill={color}
                                rx="4"
                                style={{
                                    transition: 'opacity 0.3s ease',
                                    cursor: 'pointer'
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.opacity = '0.8'}
                                onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                            />

                            {/* Value label on top of bar */}
                            <text
                                x={x + barWidth / 2}
                                y={barY - 8}
                                textAnchor="middle"
                                style={{
                                    fontSize: '13px',
                                    fontWeight: '600',
                                    fill: '#2c3e50'
                                }}
                            >
                                {bar.displayValue}
                            </text>

                            {/* Label below bar */}
                            <text
                                x={x + barWidth / 2}
                                y={chartHeight + 20}
                                textAnchor="middle"
                                style={{
                                    fontSize: '12px',
                                    fill: '#7f8c8d',
                                    fontWeight: '500'
                                }}
                            >
                                {bar.label.split(' ').map((word, i) => (
                                    <tspan key={i} x={x + barWidth / 2} dy={i === 0 ? 0 : 14}>
                                        {word}
                                    </tspan>
                                ))}
                            </text>
                        </g>
                    );
                })}

                {/* Base line */}
                <line
                    x1={0}
                    y1={chartHeight}
                    x2={chartWidth}
                    y2={chartHeight}
                    stroke="#ecf0f1"
                    strokeWidth="2"
                />
            </svg>
        </div>
    );
}

function ProjectionsChart({ data, name }) {
    // Updated modern color palette
    const barColors = ['#0084ff', '#fd002a', '#00d4aa', '#ffa500'];
    const lineColors = ['#7c3aed', '#06b6d4', '#f97316', '#8b5cf6'];

    // Parse numeric values
    const parseValue = (val) => {
        if (typeof val === 'number') return val;
        return parseFloat(val.replace(/[$,%]/g, '').replace(/,/g, '')) || 0;
    };

    // Separate data into money (bars) and percentage (lines) series
    const moneySeries = [];
    const percentageSeries = [];

    data.forEach((row, index) => {
        const label = row[0];
        const values = row.slice(1);

        // Check if this is a percentage row
        const isPercentage = values.some(v => String(v).includes('%')) ||
            label.toLowerCase().includes('%') ||
            label.toLowerCase().includes('percent');

        const numericValues = values.map(parseValue);

        if (isPercentage) {
            percentageSeries.push({
                label,
                values,
                numericValues,
                color: lineColors[percentageSeries.length % lineColors.length]
            });
        } else {
            moneySeries.push({
                label,
                values,
                numericValues,
                color: barColors[moneySeries.length % barColors.length]
            });
        }
    });

    // Generate period labels (Year 0, Year 1, etc.)
    const periods = data[0].slice(1).map((_, i) => `Year ${i}`);

    // Build chart data
    const chartData = periods.map((period, i) => {
        const dataPoint = { year: period };

        moneySeries.forEach((serie) => {
            dataPoint[serie.label] = serie.numericValues[i];
        });

        percentageSeries.forEach((serie) => {
            dataPoint[serie.label] = serie.numericValues[i];
        });

        return dataPoint;
    });

    const formatCurrency = (value) => {
        return `$${(value / 1000).toFixed(0)}k`;
    };

    const formatPercentage = (value) => {
        return `${value.toFixed(0)}%`;
    };

    return (
        <div style={{
            background: '#ffffff',
            borderRadius: '16px',
            boxShadow: '0 10px 40px rgba(0, 0, 0, 0.08), 0 2px 8px rgba(0, 0, 0, 0.04)',
            border: '1px solid rgba(0, 0, 0, 0.05)'
        }}>
            <h3 style={{
                margin: '0 0 20px 0',
                fontSize: '14px',
                fontWeight: 600,
                color: 'rgb(44, 62, 80)',
                padding: '28px 21px',
            }}>
                {name}
            </h3>

            <ResponsiveContainer width="100%" height={320}>
                <ComposedChart
                    data={chartData}
                    margin={{ top: 30, right: 70, left: 70, bottom: 30 }}
                >
                    <defs>
                        {moneySeries.map((serie, idx) => (
                            <linearGradient key={`barGradient-${idx}`} id={`barGradient-${idx}`} x1="0" y1="0" x2="0" y2="1">
                                <stop offset="0%" stopColor={serie.color} stopOpacity={0.9} />
                                <stop offset="100%" stopColor={serie.color} stopOpacity={0.5} />
                            </linearGradient>
                        ))}
                    </defs>

                    <CartesianGrid
                        strokeDasharray="3 4"
                        stroke="#e2e8f0"
                        vertical={false}
                        opacity={0.6}
                    />

                    <XAxis
                        dataKey="year"
                        tick={{ fontSize: 13, fill: '#64748b', fontWeight: 500 }}
                        axisLine={{ stroke: '#cbd5e1', strokeWidth: 1.5 }}
                        tickLine={false}
                    />

                    {/* Left Y-axis for money values */}
                    <YAxis
                        yAxisId="left"
                        tick={{ fontSize: 12, fill: '#0084ff', fontWeight: 600 }}
                        axisLine={{ stroke: '#0084ff', strokeWidth: 2 }}
                        tickLine={false}
                        tickFormatter={formatCurrency}
                        label={{
                            value: "Amount ($)",
                            angle: -90,
                            position: "insideLeft",
                            offset: -10,
                            style: { fill: '#0084ff', fontSize: 12, fontWeight: 700 }
                        }}
                    />

                    {/* Right Y-axis for percentage values */}
                    {percentageSeries.length > 0 && (
                        <YAxis
                            yAxisId="right"
                            orientation="right"
                            tick={{ fontSize: 12, fill: '#7c3aed', fontWeight: 600 }}
                            axisLine={{ stroke: '#7c3aed', strokeWidth: 2 }}
                            tickLine={false}
                            tickFormatter={formatPercentage}
                            label={{
                                value: "Percentage (%)",
                                angle: 90,
                                position: "insideRight",
                                offset: -10,
                                style: { fill: '#7c3aed', fontSize: 12, fontWeight: 700 }
                            }}
                        />
                    )}

                    <Tooltip
                        contentStyle={{
                            backgroundColor: "rgba(15, 23, 42, 0.95)",
                            border: "1px solid rgba(148, 163, 184, 0.2)",
                            borderRadius: "12px",
                            fontSize: "13px",
                            padding: "12px 16px",
                            boxShadow: "0 10px 30px rgba(0, 0, 0, 0.2)"
                        }}
                        labelStyle={{ color: '#e2e8f0', fontWeight: 600, marginBottom: '8px' }}
                        itemStyle={{ color: '#e2e8f0', padding: '4px 0' }}
                        formatter={(value, name) => {
                            const isPercentage = percentageSeries.some(s => s.label === name);
                            const formatted = isPercentage ? formatPercentage(value) : formatCurrency(value);
                            return [formatted, name];
                        }}
                    />

                    <Legend
                        wrapperStyle={{
                            fontSize: 13,
                            paddingTop: "16px",
                            color: '#475569'
                        }}
                        height={35}
                        iconType="square"
                    />

                    {/* Render bars for money values */}
                    {moneySeries.map((serie, idx) => (
                        <Bar
                            key={`bar-${idx}`}
                            yAxisId="left"
                            dataKey={serie.label}
                            fill={`url(#barGradient-${idx})`}
                            radius={[3, 3, 0, 0]}
                            isAnimationActive={true}
                            animationDuration={800}
                        />
                    ))}

                    {/* Render lines for percentage values */}
                    {percentageSeries.map((serie, idx) => (
                        <Line
                            key={`line-${idx}`}
                            yAxisId="right"
                            type="natural"
                            dataKey={serie.label}
                            stroke={serie.color}
                            strokeWidth={4}
                            dot={{
                                fill: "white",
                                stroke: serie.color,
                                strokeWidth: 3,
                                r: 6,
                                shadow: `0 2px 8px ${serie.color}40`
                            }}
                            activeDot={{
                                r: 8,
                                filter: `drop-shadow(0 0 8px ${serie.color}60)`
                            }}
                            isAnimationActive={true}
                            animationDuration={800}
                        />
                    ))}
                </ComposedChart>
            </ResponsiveContainer>
        </div>
    );
}